import app from 'flarum/forum/app';
import Page from 'flarum/common/components/Page';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import Placeholder from 'flarum/common/components/Placeholder';
import Button from 'flarum/common/components/Button';
import Discussion from 'flarum/common/models/Discussion';
import PaintingsGalleryModal from './PaintingsGalleryModal';

// Same shape champis-species' extend.php returns for Discussion::species()
// - a cross-extension attribute this extension depends on implicitly
// (schema-level, not composer-level), same coupling as champis-forum's
// DiscussionFilters.js.

const PAGE_LIMIT = 50;
const VISIBLE_STEP = 30;
const AQUARELLES_TAG_SLUG = 'aquarelles';

// Strips accents for the sort so it matches how a French speaker expects
// "Périgord" to sort next to "Pazmany" rather than after "Zzz" - same
// helper as champis-species' DescriptionsPage.js and champis-keys'
// DeterminationKeysPage.js.
function normalize(text) {
  return text.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase();
}

// Paintings without a resolved species (rare - a live GBIF lookup failure,
// see item()'s own null guard) sort last rather than first, so a missing
// name doesn't jump the queue ahead of everything alphabetical.
const NO_SPECIES_SORT_KEY = String.fromCharCode(0xffff);

// champis-species' Species::insertRankMarker() (PHP) splices "var."/"f."/
// "subsp." into canonicalName between the two epithets it belongs between,
// so e.g. a form and a variety of the same infraspecific epithet read as
// different names. Sorting on that as-is would group entries by marker
// letter first ("f." before "subsp." before "var."), splitting up what
// should otherwise interleave as one alphabetical run by epithet - stripped
// back out here for the sort key only, the *displayed* caption keeps it.
function sortKey(discussion) {
  const species = discussion.species() && discussion.species()[0];
  return species ? normalize(species.canonicalName.replace(/\s(subsp\.|var\.|f\.)\s/, ' ')) : NO_SPECIES_SORT_KEY;
}
const RANK_MARKER = /\s(subsp\.|var\.|f\.)\s/;

// Local copy of champis-species' speciesName.js - can't import across
// extension packages, same reason normalize() above is duplicated rather
// than shared. Renders the name in italics (marker upright when
// canonicalName has one - see that file's own docblock for why) with the
// author alongside, upright; falls back to the whole scientificName when
// canonicalName is blank (SpeciesCorrectionLookup::addCfWithNames() can
// still legitimately return one blank - unknown rank - even after its own
// name/author-blank-together fix) rather than risking an author with no
// name next to it, which is the bug this replaces the previous plain
// `<em>{canonicalName}</em>` markup to fix.
function paintingSpeciesName(species) {
  if (species.canonicalName) {
    const markerMatch = species.canonicalName.match(RANK_MARKER);
    return markerMatch ? m("span", null, m("em", null, species.canonicalName.slice(0, markerMatch.index)), " ", markerMatch[1], ' ', m("em", null, species.canonicalName.slice(markerMatch.index + markerMatch[0].length)), species.author && m("small", null, " ", species.author)) : m("span", null, m("em", null, species.canonicalName), species.author && m("small", null, " ", species.author));
  }
  return m("em", null, species.scientificName);
}

/**
 * /paintings - every discussion tagged "Aquarelles" that has an image on
 * its first post (see the `paintingImage` field on DiscussionResource,
 * extend.php), sorted alphabetically by species name: a grid of
 * thumbnails, each captioned with its species name (+ French vernacular
 * name when GBIF/TAXREF has one, via the same opt-in `vernacularName=true`
 * request param champis-species' own /descriptions uses). Clicking a
 * thumbnail opens PaintingsGalleryModal to browse the whole loaded set
 * full-size (next/previous, wraps around, arrow keys) - in the same
 * alphabetical order as the grid.
 *
 * Same "load everything up front, VISIBLE_STEP only gates what's
 * rendered" approach as /descriptions and /keys (see those pages' own
 * docblocks for why - a few hundred discussions is small enough to hold
 * entirely in memory, and it lets the gallery modal browse the complete
 * set rather than just whatever's currently on screen).
 */
export default class PaintingsPage extends Page {
  discussions = null;
  error = false;
  visibleCount = (() => VISIBLE_STEP)();
  oninit(vnode) {
    super.oninit(vnode);
    app.setTitle(app.translator.trans('champis-paintings.forum.paintings_page.title'));
    this.discussions = null;
    this.error = false;
    this.visibleCount = VISIBLE_STEP;
    this.loadAll();
  }
  loadAll() {
    this.discussions = null;
    this.error = false;
    const url = app.forum.attribute('apiUrl') + '/discussions';
    const pageParams = offset => ({
      filter: {
        tag: AQUARELLES_TAG_SLUG
      },
      page: {
        limit: PAGE_LIMIT,
        offset
      },
      vernacularName: 'true',
      withPaintingImages: 'true'
    });

    // Each discussion's `species` field can mean a live GBIF lookup
    // server-side (cache-cold worst case ~200-500ms *per discussion*, see
    // champis-species' DiscussionResource field), so once the first page
    // reports the real total (`meta.page.total` - a plain field, unlike
    // `links.next`, which comes back missing the `/api` prefix in this
    // environment and 404s), every remaining page is requested
    // *concurrently* rather than one after another: wall-clock cost becomes
    // the slowest single page rather than the sum of all of them. Sorting
    // only once everything is back also avoids re-sorting (and visibly
    // reshuffling on screen) after every page the way fetching them one at
    // a time did before.
    app.request({
      method: 'GET',
      url,
      params: pageParams(0)
    }).then(firstPayload => {
      const total = firstPayload.meta?.page?.total ?? 0;
      const firstPage = app.store.pushPayload(firstPayload);
      const remainingOffsets = [];
      for (let offset = PAGE_LIMIT; offset < total; offset += PAGE_LIMIT) {
        remainingOffsets.push(offset);
      }
      Promise.all(remainingOffsets.map(offset => app.request({
        method: 'GET',
        url,
        params: pageParams(offset)
      }))).then(remainingPayloads => {
        const all = remainingPayloads.reduce((acc, payload) => acc.concat(app.store.pushPayload(payload)), firstPage);
        this.discussions = all.filter(d => d.paintingImage()).sort((a, b) => sortKey(a).localeCompare(sortKey(b)));
        m.redraw();
      }).catch(() => {
        this.error = true;
        m.redraw();
      });
    }).catch(() => {
      this.error = true;
      m.redraw();
    });
  }
  view() {
    const rows = this.discussions || [];
    const visible = rows.slice(0, this.visibleCount);
    return m("div", {
      className: "Page PaintingsPage"
    }, m("div", {
      className: "container"
    }, m("h1", {
      className: "ChampisDiscussionList-header"
    }, m("i", {
      className: "icon fas fa-palette",
      "aria-hidden": "true"
    }), " ", app.translator.trans('champis-paintings.forum.paintings_page.title')), m("div", {
      className: "ChampisPaintingsList"
    }, !this.discussions && this.error ? m(Placeholder, {
      text: app.translator.trans('champis-paintings.forum.paintings_page.error')
    }) : !this.discussions ? m(LoadingIndicator, null) : rows.length === 0 ? m(Placeholder, {
      text: app.translator.trans('champis-paintings.forum.paintings_page.empty')
    }) : visible.map((discussion, index) => this.item(discussion, index))), visible.length < rows.length && m("div", {
      className: "ChampisPaintingsList-loadMore"
    }, m(Button, {
      className: "Button",
      onclick: () => this.visibleCount += VISIBLE_STEP
    }, app.translator.trans('champis-paintings.forum.paintings_page.load_more_button')))));
  }

  // Whole card opens the gallery modal (see openGallery()) rather than
  // navigating anywhere - unlike the site's other lists there's no
  // separate detail page for a painting to link to.
  item(discussion, index) {
    const image = discussion.paintingImage();
    const species = discussion.species() && discussion.species()[0];
    return m("a", {
      key: discussion.id(),
      href: "#",
      className: "ChampisPaintingsList-item",
      title: image.baseName,
      onclick: e => {
        e.preventDefault();
        this.openGallery(index);
      }
    }, m("img", {
      className: "ChampisPaintingsList-image",
      src: image.thumbnailUrl,
      alt: image.baseName,
      loading: "lazy"
    }), species && m("span", {
      className: "ChampisPaintingsList-caption"
    }, m("span", {
      className: "ChampisPaintingsList-name"
    }, paintingSpeciesName(species)), species.vernacularName && m("span", {
      className: "ChampisPaintingsList-vernacular"
    }, species.vernacularName)));
  }

  // index is into this.discussions (the whole loaded set, not just what's
  // currently rendered) - visible is always a prefix of it (see view()),
  // so the same index lines up in both; the modal browses everything
  // loaded so far, not just the on-screen subset.
  openGallery(index) {
    const photos = (this.discussions ?? []).map(discussion => {
      const image = discussion.paintingImage();
      const species = discussion.species() && discussion.species()[0];
      return {
        url: image.url,
        baseName: image.baseName,
        caption: image.caption,
        discussionId: discussion.id() ?? '',
        canonicalName: species ? species.canonicalName : null,
        vernacularName: species ? species.vernacularName : null
      };
    });
    app.modal.show(PaintingsGalleryModal, {
      photos,
      index
    });
  }
}
flarum.reg.add('champis-paintings', 'forum/components/PaintingsPage', PaintingsPage);